import { Appointment } from '../components/dashboard/widgets/types/appointment';

interface ReminderSchedule {
  appointmentId: string;
  appointment: Appointment;
  reminderTime: Date;
  timeoutId: NodeJS.Timeout;
  snoozedCount: number;
}

type ReminderCallback = (appointment: Appointment) => void;

class AppointmentRemindersService {
  private scheduledReminders: Map<string, ReminderSchedule> = new Map();
  private reminderCallback: ReminderCallback | null = null;

  /**
   * Set the callback function that will be called when a reminder is triggered
   */
  setReminderCallback(callback: ReminderCallback) {
    this.reminderCallback = callback;
  }

  /**
   * Schedule all reminders for an appointment
   */
  scheduleReminder(appointment: Appointment) {
    // Clear existing reminder if any
    this.clearReminder(appointment.id);

    // If no reminder set, skip
    if (!appointment.reminder || appointment.reminder === 0) {
      return;
    }

    // Calculate reminder time
    const appointmentDateTime = new Date(`${appointment.date}T${appointment.startTime}`);
    const reminderTime = new Date(appointmentDateTime.getTime() - appointment.reminder * 60 * 1000);
    const now = new Date();

    // Only schedule if reminder time is in the future
    if (reminderTime > now) {
      const delay = reminderTime.getTime() - now.getTime();

      const timeoutId = setTimeout(() => {
        this.triggerReminder(appointment);
      }, delay);

      this.scheduledReminders.set(appointment.id, {
        appointmentId: appointment.id,
        appointment,
        reminderTime,
        timeoutId,
        snoozedCount: 0
      });

      console.log(
        `📅 Erinnerung geplant für "${appointment.title}" um ${reminderTime.toLocaleTimeString('de-DE')} (in ${appointment.reminder} Min)`
      );
    }
  }

  /**
   * Trigger the reminder notification
   */
  private triggerReminder(appointment: Appointment) {
    console.log(`🔔 Erinnerung ausgelöst: "${appointment.title}"`);

    if (this.reminderCallback) {
      this.reminderCallback(appointment);
    }

    // Remove from scheduled reminders
    this.scheduledReminders.delete(appointment.id);
  }

  /**
   * Snooze a reminder for additional minutes
   */
  snoozeReminder(appointmentId: string, minutes: number) {
    const schedule = this.scheduledReminders.get(appointmentId);

    if (!schedule) {
      console.warn(`Keine aktive Erinnerung für Termin ${appointmentId}`);
      return;
    }

    // Clear existing timeout
    clearTimeout(schedule.timeoutId);

    // Calculate new reminder time
    const newReminderTime = new Date(Date.now() + minutes * 60 * 1000);
    const delay = newReminderTime.getTime() - Date.now();

    const timeoutId = setTimeout(() => {
      this.triggerReminder(schedule.appointment);
    }, delay);

    // Update schedule
    this.scheduledReminders.set(appointmentId, {
      ...schedule,
      reminderTime: newReminderTime,
      timeoutId,
      snoozedCount: schedule.snoozedCount + 1
    });

    console.log(
      `⏰ Erinnerung verschoben für "${schedule.appointment.title}" auf ${newReminderTime.toLocaleTimeString('de-DE')} (+${minutes} Min)`
    );
  }

  /**
   * Clear a specific reminder
   */
  clearReminder(appointmentId: string) {
    const schedule = this.scheduledReminders.get(appointmentId);

    if (schedule) {
      clearTimeout(schedule.timeoutId);
      this.scheduledReminders.delete(appointmentId);
      console.log(`🗑️ Erinnerung gelöscht für Termin ${appointmentId}`);
    }
  }

  /**
   * Clear all reminders
   */
  clearAllReminders() {
    this.scheduledReminders.forEach((schedule) => {
      clearTimeout(schedule.timeoutId);
    });
    this.scheduledReminders.clear();
    console.log('🗑️ Alle Erinnerungen gelöscht');
  }

  /**
   * Reschedule all reminders from storage
   */
  async rescheduleAllFromStorage() {
    try {
      // Termine liegen in der Datenbank (nicht mehr in localStorage!) —
      // aktuelle + kommende Termine aus der API laden, sonst sind nach
      // jedem Seiten-Reload alle Erinnerungen verloren.
      const { default: appointmentsService } = await import('./appointments.service');
      const now = new Date();
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const [curr, next] = await Promise.all([
        appointmentsService.getByMonth(now.getFullYear(), now.getMonth()),
        appointmentsService.getByMonth(nextMonth.getFullYear(), nextMonth.getMonth()),
      ]);
      const appointments: Appointment[] = [
        ...(curr?.data || []),
        ...(next?.data || []),
      ];

      // Clear existing reminders
      this.clearAllReminders();

      // Schedule reminders for all future appointments
      appointments.forEach((appointment) => {
        const appointmentDateTime = new Date(`${appointment.date}T${appointment.startTime}`);

        // Only schedule for future appointments
        if (appointmentDateTime > now) {
          this.scheduleReminder(appointment);
        }
      });

      console.log(`📅 ${this.scheduledReminders.size} Erinnerungen neu geplant`);
    } catch (error) {
      console.error('Fehler beim Neu-Planen der Erinnerungen:', error);
    }
  }

  /**
   * Get all scheduled reminders
   */
  getScheduledReminders(): ReminderSchedule[] {
    return Array.from(this.scheduledReminders.values());
  }

  /**
   * Check if a reminder is scheduled for an appointment
   */
  hasReminder(appointmentId: string): boolean {
    return this.scheduledReminders.has(appointmentId);
  }

  /**
   * Request notification permission from browser
   */
  async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('Dieser Browser unterstützt keine Benachrichtigungen');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  }

  /**
   * Show browser notification (as backup/supplement to in-app notification)
   */
  showBrowserNotification(appointment: Appointment) {
    if (Notification.permission === 'granted') {
      const notification = new Notification(`Terminerinnerung: ${appointment.title}`, {
        body: `${appointment.startTime} - ${appointment.endTime}${appointment.location ? `\n📍 ${appointment.location}` : ''}`,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: appointment.id,
        requireInteraction: true,
        silent: false
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  }
}

// Export singleton instance
export const appointmentRemindersService = new AppointmentRemindersService();
